// Shared, restrained, NON-GAMIFIED developmental presentation used both in the classroom "My Development"
// page and embedded in the end-of-assignment Final Review. Renders WITHIN-ASSIGNMENT development for
// Writing (WSR) and Organization of Thinking (IST) as SEPARATE sections, each Beginning -> Final ->
// Development. No single/composite score, no badges/points/percentiles. Numbers are secondary to a
// plain-language interpretation; decline and insufficient data are shown honestly and framed task-bound.
import { useState } from "react";
import { ArrowUp, ArrowRight, ArrowDown, Minus, PenLine, Brain } from "lucide-react";

const BRAND = "#8C3A2A";

// Direction tokens (calm, never alarmist). Keyed by backend direction.code for BOTH writing and thinking.
const DIR = {
  developed: { Icon: ArrowUp, text: "text-emerald-700", bg: "bg-emerald-50", border: "border-emerald-200" },
  more_organized: { Icon: ArrowUp, text: "text-emerald-700", bg: "bg-emerald-50", border: "border-emerald-200" },
  same: { Icon: ArrowRight, text: "text-stone-600", bg: "bg-stone-100", border: "border-stone-200" },
  lower: { Icon: ArrowDown, text: "text-amber-700", bg: "bg-amber-50", border: "border-amber-200" },
  less_organized: { Icon: ArrowDown, text: "text-amber-700", bg: "bg-amber-50", border: "border-amber-200" },
  insufficient: { Icon: Minus, text: "text-stone-400", bg: "bg-stone-100", border: "border-stone-200" },
};

const DirPill = ({ direction, testid }) => {
  const meta = DIR[direction?.code] || DIR.insufficient;
  const Icon = meta.Icon;
  return (
    <span data-testid={testid} className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[12px] font-medium ${meta.text} ${meta.bg} ${meta.border}`}>
      <Icon className="h-3.5 w-3.5" aria-hidden="true" />
      {direction?.label || "Insufficient information"}
    </span>
  );
};

const SectionLabel = ({ children }) => (
  <p className="font-mono-panel text-[10px] sm:text-[11px] uppercase tracking-[0.18em] font-medium text-stone-400">{children}</p>
);

function WritingSection({ writing }) {
  const [showEvidence, setShowEvidence] = useState(false);
  if (!writing) return null;
  return (
    <section data-testid="development-writing" className="space-y-3">
      <div className="flex items-center gap-2">
        <PenLine className="h-4 w-4" style={{ color: BRAND }} aria-hidden="true" />
        <h3 className="font-serif-display text-lg sm:text-xl font-medium text-stone-900">Writing</h3>
      </div>
      <p className="text-[13px] leading-relaxed text-stone-600">How the structure of your writing developed from your first version to your final one. {writing.note}</p>

      {!writing.has_development_data ? (
        <p data-testid="writing-nodata" className="rounded-md bg-stone-50 border border-stone-200 p-3 text-[13px] text-stone-600">
          There aren&rsquo;t enough analysed versions yet to show how your writing developed. It will appear here once your writing has been reviewed.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table data-testid="wsr-development-table" className="w-full min-w-[560px] border-collapse text-left text-[13px]">
            <caption className="sr-only">How each writing feature changed from your beginning version to your final version.</caption>
            <thead>
              <tr className="border-b border-stone-200 text-[10px] uppercase tracking-[0.14em] text-stone-400">
                <th scope="col" className="py-2 pr-3 font-medium">Writing feature</th>
                <th scope="col" className="py-2 pr-3 font-medium">Beginning</th>
                <th scope="col" className="py-2 pr-3 font-medium">Final</th>
                <th scope="col" className="py-2 pr-3 font-medium">Development</th>
              </tr>
            </thead>
            <tbody>
              {writing.dimensions.map((d) => (
                <tr key={d.key} data-testid={`wsr-row-${d.key}`} className="border-b border-stone-100 align-top">
                  <th scope="row" className="py-3 pr-3 font-medium text-stone-800">{d.label}</th>
                  <td className="py-3 pr-3 text-stone-700">
                    {d.entry_band || <span className="text-stone-400">Insufficient</span>}
                    {d.entry_measure != null && <span className="block font-mono-panel text-[10px] text-stone-400 normal-case tracking-normal">measure {d.entry_measure.toFixed(1)}</span>}
                  </td>
                  <td className="py-3 pr-3 text-stone-700">
                    {d.final_band || <span className="text-stone-400">Insufficient</span>}
                    {d.final_measure != null && <span className="block font-mono-panel text-[10px] text-stone-400 normal-case tracking-normal">measure {d.final_measure.toFixed(1)}</span>}
                  </td>
                  <td className="py-3 pr-3"><DirPill direction={d.direction} testid={`wsr-dir-${d.key}`} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {(writing.evidence?.beginning || writing.evidence?.final) && (
        <div>
          <button
            data-testid="wsr-evidence-toggle"
            aria-expanded={showEvidence}
            aria-controls="wsr-evidence"
            onClick={() => setShowEvidence((v) => !v)}
            className="rounded-sm border border-stone-300 px-3 py-1.5 text-[13px] text-stone-700 hover:border-[#8C3A2A] hover:text-[#8C3A2A] focus:outline-none focus:ring-2 focus:ring-[#8C3A2A]"
          >
            {showEvidence ? "Hide examples" : "Show me examples"}
          </button>
          {showEvidence && (
            <div id="wsr-evidence" data-testid="wsr-evidence" className="mt-2 grid gap-3 sm:grid-cols-2">
              <div className="rounded-md border border-stone-200 bg-white p-3">
                <SectionLabel>Beginning</SectionLabel>
                <p className="mt-1 whitespace-pre-wrap text-[13px] text-stone-800 font-serif-display">{writing.evidence.beginning || "—"}</p>
              </div>
              <div className="rounded-md border border-stone-200 bg-white p-3" style={{ borderTopColor: BRAND, borderTopWidth: 2 }}>
                <SectionLabel>Final</SectionLabel>
                <p className="mt-1 whitespace-pre-wrap text-[13px] text-stone-800 font-serif-display">{writing.evidence.final || "—"}</p>
              </div>
            </div>
          )}
        </div>
      )}
    </section>
  );
}

function ThinkingSection({ thinking }) {
  if (!thinking) return null;
  const b = thinking.beginning;
  const f = thinking.final;
  return (
    <section data-testid="ist-development-block" className="space-y-3">
      <div className="flex items-center gap-2">
        <Brain className="h-4 w-4" style={{ color: BRAND }} aria-hidden="true" />
        <h3 className="font-serif-display text-lg sm:text-xl font-medium text-stone-900">Organization of Thinking</h3>
      </div>

      {!thinking.has_thinking_data ? (
        <p data-testid="ist-nodata" className="rounded-md bg-stone-50 border border-stone-200 p-3 text-[13px] text-stone-600">
          The organization of your thinking for this assignment hasn&rsquo;t been analysed yet. It will appear here once your work has been reviewed.
        </p>
      ) : (
        <div data-testid="ist-card" className="rounded-r-lg border border-stone-200 border-l-4 bg-white shadow-sm p-5" style={{ borderLeftColor: BRAND }}>
          <div className="grid gap-4 sm:grid-cols-[1fr_auto_1fr] sm:items-stretch">
            <div>
              <SectionLabel>Beginning</SectionLabel>
              <p data-testid="ist-beginning-organization" className="mt-1 text-[14px] leading-relaxed text-stone-800">
                You {b?.organization || "—"}.
              </p>
              {b?.measure != null && <span className="mt-1 block font-mono-panel text-[10px] text-stone-400 normal-case tracking-normal">measure {b.measure.toFixed(1)}</span>}
            </div>
            <div className="hidden sm:flex items-center justify-center text-stone-300">
              <ArrowRight className="h-5 w-5" />
            </div>
            {thinking.single_point ? (
              <div>
                <SectionLabel>Final</SectionLabel>
                <p data-testid="ist-final-organization" className="mt-1 text-[13px] leading-relaxed text-stone-500 italic">
                  Only one version was analysed, so there isn&rsquo;t a later point to compare with yet.
                </p>
              </div>
            ) : (
              <div>
                <SectionLabel>Final</SectionLabel>
                <p data-testid="ist-final-organization" className="mt-1 text-[14px] leading-relaxed text-stone-800">
                  You {f?.organization || "—"}.
                </p>
                {f?.measure != null && <span className="mt-1 block font-mono-panel text-[10px] text-stone-400 normal-case tracking-normal">measure {f.measure.toFixed(1)}</span>}
              </div>
            )}
          </div>

          {!thinking.single_point && (
            <div className="mt-4 pt-4 border-t border-stone-100 flex flex-wrap items-center gap-2">
              <SectionLabel>What changed</SectionLabel>
              <DirPill direction={thinking.direction} testid="ist-shift-description" />
            </div>
          )}
        </div>
      )}

      <p data-testid="ist-taskbound-note" className="text-[12px] leading-relaxed text-stone-500">{thinking.note}</p>
    </section>
  );
}

// YOUR DEVELOPMENT AS A WRITER — per structural skill: what you can do now · with guidance · growing independence · next step.
// Plain language only; condition of performance always stated; Level-5 modeled structure never reported as independent skill.
function StructuralSkillsSection({ skills }) {
  if (!skills || !skills.skills || !skills.skills.length) return null;
  const Row = ({ label, text, testid }) => text ? (
    <div data-testid={testid} className="grid grid-cols-1 sm:grid-cols-[11rem_1fr] gap-x-4 gap-y-0.5">
      <dt className="text-[12px] uppercase tracking-[0.12em] text-stone-500 pt-0.5">{label}</dt>
      <dd className="text-[14px] leading-relaxed text-stone-800">{text}</dd>
    </div>
  ) : null;
  return (
    <section data-testid="structural-skills-section" className="space-y-5">
      <div>
        <SectionLabel>Your development as a writer</SectionLabel>
        <h3 className="mt-1 font-serif-display text-xl sm:text-2xl font-semibold tracking-tight text-stone-900">What you can do — on your own and with guidance</h3>
      </div>
      {skills.skills.map((sk, i) => (
        <div key={sk.skill} data-testid={`skill-${i}`} className="rounded-lg border border-stone-200 bg-white p-5">
          <p className="text-[12px] font-semibold uppercase tracking-[0.12em] text-[#8C3A2A]">{sk.skill}</p>
          {sk.insufficient ? (
            <p data-testid={`skill-${i}-insufficient`} className="mt-2 text-[14px] text-stone-600">{sk.note}</p>
          ) : (
            <dl className="mt-3 space-y-2">
              <Row label="What you can do now" text={sk.can_do_now} testid={`skill-${i}-now`} />
              <Row label="With guidance" text={sk.with_guidance} testid={`skill-${i}-guidance`} />
              <Row label="Growing independence" text={sk.growing_independence} testid={`skill-${i}-growing`} />
              <Row label="Next step" text={sk.next_step} testid={`skill-${i}-next`} />
              {sk.level5_note ? <Row label="Note" text={sk.level5_note} testid={`skill-${i}-level5`} /> : null}
            </dl>
          )}
        </div>
      ))}
      <p data-testid="structural-skills-note" className="text-[12px] leading-relaxed text-stone-500">{skills.note}</p>
    </section>
  );
}

export default function DevelopmentReport({ data, embedded = false }) {
  if (!data) return null;
  return (
    <div
      data-testid={embedded ? "episode-review-development-block" : "development-report"}
      className={embedded ? "mt-8 border-t border-stone-200 pt-8 space-y-8" : "space-y-8"}
    >
      {embedded && (
        <div>
          <SectionLabel>Your development this assignment</SectionLabel>
          <h2 className="mt-1 font-serif-display text-2xl sm:text-3xl font-semibold tracking-tight text-stone-900">How your writing and thinking developed</h2>
        </div>
      )}
      <WritingSection writing={data.writing} />
      <ThinkingSection thinking={data.thinking} />
      <StructuralSkillsSection skills={data.structural_skills} />
    </div>
  );
}
